import React from "react";
import { FormattedMessage, useIntl } from "react-intl";
import * as d3 from "d3-array";

import Box from "@mui/material/Box";
import TextField from "@mui/material/TextField";
import Divider from "@mui/material/Divider";

import { TCatalogEntities } from "types";

import Pipeline from "../Pipeline";

import List, { CollapsedListFilter } from "../../List";
import Filter from "../../Filter";

import Reducer from "./AusstattungReducer";
import Indicator from "./AusstattungIndicator";
import ListItemIcon from "@mui/material/ListItemIcon";
import Checkbox from "@mui/material/Checkbox";
import ListItemText from "@mui/material/ListItemText";
import ListItem from "@mui/material/ListItem";

interface ISearchField {
  value: string;
  onChange: any;
  label?: any;
}

export const SearchField: React.FC<ISearchField> = ({
  value,
  onChange,
  label = "Suche",
}): JSX.Element => (
  <TextField
    fullWidth
    value={value}
    onChange={onChange}
    label={label}
    type="search"
    variant="outlined"
    size="small"
  />
);

interface IFilter {
  entities: TCatalogEntities;
  value: number[];
  dispatchFilter?: any;
}

export const AusstattungFilter: React.FC<IFilter> = ({
  entities: {
    vehicle,
    ausstattung: ausstattungEntities,
    ausstattungsgruppe: ausstattungsgruppeEntities,
  },
  value = [],
  dispatchFilter = () => null,
}): JSX.Element | null => {
  const [search, setSearch] = React.useState("");
  const { formatMessage: t } = useIntl();

  const changeSearch = (event: {
    target: { value: React.SetStateAction<string> };
  }) => {
    setSearch(event.target.value);
  };

  if (!(vehicle && ausstattungEntities && ausstattungsgruppeEntities))
    return null;

  const ausstattungIds = Object.values(ausstattungEntities).map(
    (e: any) => e.id,
  );

  const setValueHandler = (value: any) => {
    if (value.length === ausstattungIds.length)
      dispatchFilter({ type: "UPDATE_FILTER", payload: { ausstattung: [] } });
    else
      dispatchFilter({
        type: "UPDATE_FILTER",
        payload: { ausstattung: value },
      });
  };

  const selectAll = () => {
    dispatchFilter({ type: "UPDATE_FILTER", payload: { ausstattung: [] } });
  };

  const options = ausstattungIds.map((id: number) => ({
    value: id,
    title: t({
      id: `ausstattung.${id}.label`,
      defaultMessage: ausstattungEntities[id].name,
    }),
    group: ausstattungEntities[id].ausstattungsgruppe,
  }));

  const groupedOptions = d3.group(options, (d: any) => d.group);

  const searchedOptions = options.filter(
    ({ title }: { title: string }) =>
      search.length > 1 &&
      title.toLocaleLowerCase().indexOf(search.toLocaleLowerCase()) > -1,
  );

  const Component = (
    <Box sx={{ width: 1 }}>
      <SearchField
        value={search}
        onChange={changeSearch}
        label={t({ id: "common.search", defaultMessage: "Suche" })}
      />
      <List
        value={value || []}
        options={searchedOptions}
        setValueHandler={setValueHandler}
      />
      <Divider />
      <ListItem
        id="filter-marke-model-filter-all"
        key="all"
        dense
        onClick={() => selectAll()}
        sx={{ cursor: "pointer" }}
      >
        <ListItemIcon style={{ minWidth: 40 }}>
          <Checkbox
            color="primary"
            edge="start"
            checked={!value || !value.length}
            tabIndex={-1}
            slotProps={{
              input: { "aria-labelledby": "checkbox-list-label-all" },
            }}
          />
        </ListItemIcon>
        <ListItemText className="list-item-text" primary="Alle" />
      </ListItem>
      <Divider />
      {Array.from(groupedOptions, ([key, values]) => {
        return (
          <CollapsedListFilter
            label={t({
              id: `ausstattungsgruppe.${key}.label`,
              defaultMessage: ausstattungsgruppeEntities[key].name,
            })}
            key={key}
          >
            <List
              value={value || []}
              options={values}
              setValueHandler={setValueHandler}
            />
          </CollapsedListFilter>
        );
      })}
    </Box>
  );

  return (
    <Filter
      component={Component}
      title={
        <FormattedMessage
          id="common.ausstattung"
          defaultMessage="Ausstattungen"
        />
      }
    />
  );
};

const Container = () => {
  return (
    <Pipeline
      {...{
        type: "ausstattung",
        defaultValue: [],
        Component: AusstattungFilter,
        Reducer,
        Indicator,
      }}
    />
  );
};

export default Container;
